import React, { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { formatMoneyByCurrency } from "../utils/currency";
import { adminStatusLabel, adminStatusClass } from "../utils/orderMoney";
import { requestDayBounds } from "../utils/adminOrderStats";
import type { AdminRequestRow as Order } from "../types/admin";
import StatsPending from "./admin-stats/StatsPending";
import { requestsFrom, requestsUrl } from "./admin-stats/api";
import { useAdminRead } from "../hooks/useAdminRead";
import { absMediaUrl } from "../utils/mediaUrl";
import { useTalentNames } from "../hooks/useTalentNames";

// Requests tab — every request, filtered, searched and sorted on the server
// (GET /admin/requests, server lib/admin/stats.js): one page of rows plus the
// KPIs over EVERY matching request, whatever the history's size.

interface User {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface Props {
  users: User[];
  // Bumped by the header's ↻ Refresh: read the current view again.
  refreshKey?: number;
}

export default function AdminOrdersTab({ users, refreshKey = 0 }: Props) {
  const { talent, partial } = useTalentNames(users);
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDj, setFilterDj] = useState("all");
  const [sortKey, setSortKey] = useState<"date" | "amount">("date");
  // What is in the search box, and what the server searches: the same, 300 ms
  // after the typing stops.
  const [typed, setTyped] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  // Phase 2.5 — when on, hide played orders that already have a
  // track-history file attached. Lets admin spot which talents need a nudge.
  const [filterMissingTrackHistory, setFilterMissingTrackHistory] = useState(false);

  // Stable user lookup
  const userMap = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u._id, u);
    return m;
  }, [users]);

  const resolveDj = useCallback(
    (djData: Order["dj"]) => {
      if (typeof djData === "object" && djData !== null) {
        return { name: djData.name, avatar: djData.avatarUrl, id: djData._id };
      }
      const found = userMap.get(djData);
      return { name: found?.name ?? "Unknown", avatar: found?.avatarUrl, id: djData as string };
    },
    [userMap]
  );

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);

  // The From / To days run from local midnight to 23:59:59.999, as before.
  const bounds = useMemo(() => requestDayBounds(dateFrom, dateTo), [dateFrom, dateTo]);
  const url = requestsUrl({
    status: filterStatus,
    dj: filterDj,
    q: searchQuery,
    from: bounds.from,
    to: bounds.to,
    missingTrackHistory: filterMissingTrackHistory,
    sort: sortKey,
  });
  // While a new filter loads, the last answer stays on screen (like the Fans
  // tab); a failed read shows Retry instead of rows that don't match.
  const { latest, failed, retry } = useAdminRead(url, requestsFrom, refreshKey);
  const view = failed ? null : latest;

  // Render cap: the server sends the first 500 matching rows (a DOM table of
  // the whole history makes the tab crawl); every KPI above the table covers
  // ALL matching requests, and the line under the table says so — never a
  // silent truncation.
  const visibleRows = view ? view.items : [];
  const total = view ? view.total : 0;
  const completionPct = view && total > 0 ? ((view.kpis.completed / total) * 100).toFixed(0) : "0";

  return (
    <div className="adm-tab-container fade-in">
      {/* Stats row */}
      {!view ? (
        <StatsPending failed={failed} onRetry={retry} />
      ) : (
      <div className="adm-grid-5 adm-mb-14">
        {[
          { label: "Total Revenue", value: formatMoneyByCurrency(view.kpis.grossByCurrency), color: "" },
          // No hardcoded % in the label — fees are real summed amounts and the
          // rate varies per talent (admin overrides today, loyalty tiers at
          // launch). "(15%)" would lie the moment either applies.
          { label: "Platform Fees", value: formatMoneyByCurrency(view.kpis.feesByCurrency), color: "" },
          { label: "Requests", value: String(total), color: "" },
          { label: "Avg. Request Value", value: formatMoneyByCurrency(view.kpis.avgByCurrency), color: "" },
          { label: "Completion Rate", value: `${completionPct}%`, color: "" },
        ].map((s) => (
          <div key={s.label} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value" style={s.color ? { color: s.color } : {}}>
                {s.value}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Toolbar */}
      <div className="adm-toolbar" style={{ flexWrap: "wrap" }}>
        <div className="adm-toolbar-group">
          <input
            className="adm-search"
            placeholder="Search customer, song..."
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ width: 200 }}
          />
          <select className="adm-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="selected">Selected (unpaid)</option>
            <option value="pending">Pending</option>
            <option value="queued">Queued</option>
            <option value="accepted">Accepted</option>
            <option value="played">Played</option>
            <option value="declined">Declined</option>
          </select>
          <select className="adm-select" value={filterDj} onChange={(e) => setFilterDj(e.target.value)}>
            <option value="all">All Talent</option>
            {talent.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
            {partial && <option disabled>Couldn't load the full list</option>}
          </select>
        </div>
        <div className="adm-toolbar-group">
          <span className="muted-text" style={{ fontSize: 12 }}>From:</span>
          <input type="date" className="adm-search" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ width: 130 }} />
          <span className="muted-text" style={{ fontSize: 12 }}>To:</span>
          <input type="date" className="adm-search" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ width: 130 }} />
          <span className="muted-text adm-ml-8" style={{ fontSize: 12 }}>Sort:</span>
          {(["date", "amount"] as const).map((k) => (
            <button key={k} className={`filter-chip ${sortKey === k ? "active" : ""}`} onClick={() => setSortKey(k)}>
              {k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
          <button
            className={`filter-chip ${filterMissingTrackHistory ? "active" : ""}`}
            onClick={() => setFilterMissingTrackHistory((v) => !v)}
            title="Show only played orders that don't have track-history evidence attached"
          >
            Missing track history
          </button>
        </div>
      </div>

      {/* Table — ultra compact */}
      {view && (
      <div className="adm-scrollable-full">
        <table className="adm-table-light adm-table-xs sticky-header adm-w-full" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "13%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "4%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Talent</th>
              <th>Track</th>
              <th>Client</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Paid</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((o) => {
              const dj = resolveDj(o.dj);
              const trackImg = absMediaUrl(o.trackImage);
              const isExpanded = expandedOrderId === o._id;

              return (
                <Fragment key={o._id}>
                <tr className="hover-row">
                  {/* DJ — clickable */}
                  <td>
                    <div className="dj-cell dj-cell-xs clickable" onClick={() => navigate(`/admin/dj/${dj.id}`)}>
                      {absMediaUrl(dj.avatar) ? (
                        <img src={absMediaUrl(dj.avatar)!} className="dj-avatar-xs" alt="" />
                      ) : (
                        <div className="dj-placeholder-xs">{dj.name[0].toUpperCase()}</div>
                      )}
                      <span className="dj-link-text truncate-inline">{dj.name}</span>
                    </div>
                  </td>

                  {/* Track */}
                  <td>
                    <div className="dj-cell dj-cell-xs">
                      {trackImg ? (
                        <img src={trackImg} className="track-img-xs" alt="" />
                      ) : (
                        <div className="track-placeholder-xs">♪</div>
                      )}
                      <span className="truncate-inline adm-fs-76">
                        {o.searchQuery}
                      </span>
                    </div>
                  </td>

                  {/* Client */}
                  <td
                    className="truncate-inline adm-fs-74 adm-pointer"
                    style={{ display: "table-cell" }}
                    onClick={() => setExpandedOrderId(expandedOrderId === o._id ? null : o._id)}
                  >
                    {o.customerName || (o.clientInstagram ? `@${o.clientInstagram}` : null) || o.customerEmail || o.clientNickname || (o.cardLast4 ? `•••• ${o.cardLast4}` : null) || "—"}
                    <span className="muted-text adm-ml-4" style={{ fontSize: "0.65rem" }}>
                      {expandedOrderId === o._id ? "▲" : "▼"}
                    </span>
                  </td>

                  {/* Amount */}
                  <td className="adm-bold adm-fs-78 adm-nowrap">
                    {(() => {
                      const cur = (o.currency || "usd").toUpperCase();
                      // Counter pending: DJ declined with counter, fan hasn't responded
                      if (o.counterAmount && o.declineReason === "more_money") {
                        const diff = o.counterAmount - o.amount;
                        return (
                          <>
                            {cur} {o.counterAmount.toFixed(2)}
                            <span className="amount-addon amount-counter" title={`Countered — Original: ${cur} ${o.amount.toFixed(2)}`}>
                              +{diff.toFixed(2)}
                            </span>
                          </>
                        );
                      }
                      // Counter accepted or boost: originalAmount preserved
                      if (o.originalAmount != null && o.originalAmount !== o.amount) {
                        const diff = o.amount - o.originalAmount;
                        const isBoosted = !!o.isPriority;
                        return (
                          <>
                            {cur} {o.amount.toFixed(2)}
                            <span
                              className={`amount-addon ${isBoosted ? "amount-boost" : "amount-counter"}`}
                              title={`${isBoosted ? "Boosted" : "Countered"} — Original: ${cur} ${o.originalAmount.toFixed(2)}`}
                            >
                              +{diff.toFixed(2)}
                            </span>
                          </>
                        );
                      }
                      // Fast pass: bid was 2x DJ minimum, show the split
                      if (o.tier === "fast") {
                        const half = o.amount / 2;
                        return (
                          <>
                            {cur} {o.amount.toFixed(2)}
                            <span className="amount-addon amount-fast" title={`FastPass — base ${cur} ${half.toFixed(2)} + ${cur} ${half.toFixed(2)} premium`}>+{half.toFixed(2)}</span>
                          </>
                        );
                      }
                      return <>{cur} {o.amount.toFixed(2)}</>;
                    })()}
                  </td>

                  {/* Status */}
                  <td>
                    {o.status === "declined" && o.declineReason ? (
                      <span
                        className={`badge badge-xs badge-declined adm-help`}
                        title={
                          o.declineReason === "pas_ambience" ? "Not the Vibe"
                            : o.declineReason === "no_access" ? "No Access to Song"
                            : o.declineReason === "already_played" ? "Already Played"
                            : o.declineReason === "more_money" ? "Counter Offered"
                            : o.declineReason
                        }
                      >
                        declined *
                      </span>
                    ) : (
                      <span className={`badge badge-xs badge-${adminStatusClass(o)}`}>{adminStatusLabel(o)}</span>
                    )}
                    {o.isDisputed && (
                      <span className="badge badge-xs badge-declined adm-ml-4" title="This order has been disputed">
                        Disputed
                      </span>
                    )}
                    {o.trackHistoryStripeFileId && (
                      <span
                        className="badge badge-xs badge-accepted adm-ml-4"
                        title={`Track history attached${o.trackHistoryFilename ? ` — ${o.trackHistoryFilename}` : ""}`}
                      >
                        ✓ TH
                      </span>
                    )}
                  </td>

                  {/* Date */}
                  <td className="adm-fs-72 adm-nowrap" style={{ color: "var(--adm-text-sec)" }}>
                    {new Date(o.createdAt).toLocaleDateString()}{" "}
                    {new Date(o.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </td>

                  {/* Paid */}
                  <td>
                    {o.paid && !((o.amount ?? 0) > 0) ? (
                      <span className="badge badge-xs">Free</span>
                    ) : (
                      <span className={`badge badge-xs ${o.paid ? "badge-accepted" : "badge-declined"}`}>
                        {o.paid ? "✓" : "✗"}
                      </span>
                    )}
                  </td>

                  {/* Link */}
                  <td>
                    <button
                      className="adm-btn-icon"
                      onClick={() => navigate(`/admin/dj/${dj.id}`)}
                      title={`View ${dj.name}`}
                    >
                      →
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={8} className="adm-expand-cell">
                      <div className="adm-fs-78" style={{ display: "flex", gap: 24, padding: "10px 14px" }}>
                        {[
                          { label: "Real Name", value: o.customerName },
                          { label: "Instagram", value: o.clientInstagram ? `@${o.clientInstagram}` : null },
                          { label: "Email", value: o.customerEmail },
                          { label: "Nickname", value: o.clientNickname },
                          { label: "Card", value: o.cardLast4 ? `•••• ${o.cardLast4}` : null },
                        ].map((item) => (
                          <div key={item.label}>
                            <div className="muted-text adm-eyebrow-sm">{item.label}</div>
                            <div className="adm-semibold">{item.value || "—"}</div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {total === 0 && (
              <tr>
                <td colSpan={8} className="adm-center adm-p-24 adm-grey">
                  No orders match the current filters.
                </td>
              </tr>
            )}
            {total > visibleRows.length && (
              <tr>
                <td colSpan={8} className="adm-center adm-p-14 adm-grey adm-fs-80">
                  Showing the first {visibleRows.length.toLocaleString()} of {total.toLocaleString()} matching
                  orders (stats above cover all of them) — narrow with the filters or search to see the rest.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
